import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { GlobalLoader } from './GlobalLoader';
import { ensureCsrf } from '@/api/client';
import { currentUser } from '@/api/auth';

export type LoadingPhase = 'booting' | 'ready' | 'boot-error';

type LoadingContextValue = {
  phase: LoadingPhase;
  /** True when any tracked global/blocking task is active. */
  busy: boolean;
  /** Ref-counted task registry — concurrent ops never clobber each other. */
  start: (id: string) => void;
  done: (id: string) => void;
  /** Wrap an async op so it registers under a stable id. */
  track: <T>(id: string, fn: () => Promise<T>) => Promise<T>;
  retryBoot: () => void;
};

const LoadingContext = createContext<LoadingContextValue | null>(null);

const BOOT_MIN_MS = 0;
const BOOT_TIMEOUT_MS = 12_000;

function dismissHtmlSplash() {
  const el = document.getElementById('boot-splash');
  if (!el) return;
  el.classList.add('is-done');
  window.setTimeout(() => el.remove(), 320);
}

/**
 * Global loading manager:
 * - Boot phase waits for CSRF + session user before revealing the app shell.
 * - `start`/`done` use a Map of ids → refcounts so overlapping ops stay correct.
 * - Route progress bar is independent (non-blocking) via RouteTransitionLoader.
 */
export function LoadingProvider({ children }: { children: ReactNode }) {
  const [phase, setPhase] = useState<LoadingPhase>('booting');
  const [busy, setBusy] = useState(false);
  const tasks = useRef(new Map<string, number>());
  const bootRun = useRef(0);
  const queryClient = useQueryClient();

  const syncBusy = useCallback(() => {
    setBusy(tasks.current.size > 0);
  }, []);

  const start = useCallback(
    (id: string) => {
      tasks.current.set(id, (tasks.current.get(id) ?? 0) + 1);
      syncBusy();
    },
    [syncBusy],
  );

  const done = useCallback(
    (id: string) => {
      const n = (tasks.current.get(id) ?? 1) - 1;
      if (n <= 0) tasks.current.delete(id);
      else tasks.current.set(id, n);
      syncBusy();
    },
    [syncBusy],
  );

  const track = useCallback(
    async <T,>(id: string, fn: () => Promise<T>): Promise<T> => {
      start(id);
      try {
        return await fn();
      } finally {
        done(id);
      }
    },
    [start, done],
  );

  const runBoot = useCallback(async () => {
    const run = ++bootRun.current;
    setPhase('booting');
    const started = performance.now();
    try {
      await Promise.race([
        (async () => {
          // Parallel: CSRF cookie + session probe (GET). Seed RQ cache so requireAuth is free.
          const [, user] = await Promise.all([
            ensureCsrf().catch(() => undefined),
            currentUser().catch(() => null),
          ]);
          if (user) queryClient.setQueryData(['auth', 'user'], user);
        })(),
        new Promise((_, reject) =>
          window.setTimeout(() => reject(new Error('boot-timeout')), BOOT_TIMEOUT_MS),
        ),
      ]);
      if (run !== bootRun.current) return;
      const elapsed = performance.now() - started;
      if (elapsed < BOOT_MIN_MS) {
        await new Promise((r) => window.setTimeout(r, BOOT_MIN_MS - elapsed));
      }
      if (run !== bootRun.current) return;
      setPhase('ready');
      dismissHtmlSplash();
    } catch {
      if (run !== bootRun.current) return;
      setPhase('boot-error');
      dismissHtmlSplash();
    }
  }, [queryClient]);

  useEffect(() => {
    void runBoot();
  }, [runBoot]);

  // If React unmounts mid-boot, drop stale task map (avoid leaks).
  useEffect(() => {
    return () => {
      tasks.current.clear();
      bootRun.current += 1;
    };
  }, []);

  const value = useMemo<LoadingContextValue>(
    () => ({ phase, busy, start, done, track, retryBoot: () => void runBoot() }),
    [phase, busy, start, done, track, runBoot],
  );

  return (
    <LoadingContext.Provider value={value}>
      {phase === 'booting' || phase === 'boot-error' ? (
        <GlobalLoader
          error={phase === 'boot-error'}
          onRetry={phase === 'boot-error' ? () => void runBoot() : undefined}
        />
      ) : null}
      {/* Children (RouterProvider) mount only after boot — RouteTransitionLoader lives inside the router. */}
      {phase === 'ready' ? children : null}
    </LoadingContext.Provider>
  );
}

export function useLoading(): LoadingContextValue {
  const ctx = useContext(LoadingContext);
  if (!ctx) throw new Error('useLoading must be used inside LoadingProvider');
  return ctx;
}
